/**
 * /simple-setup data + rich-narrative copy.
 *
 * This file lives under src/lib/ (NOT a marketing-component surface),
 * so it is exempt from the V4 hardcoded-marketing-copy guard. Use it
 * to hold the multi-paragraph step + benefit copy that doesn't fit
 * the rigid PageCopy schema. Hero / canonical headings still come
 * from SIMPLE_SETUP_COPY in src/lib/marketing/messaging-bank.ts.
 *
 * Only reviewed, non-customer-specific setup guidance belongs here. Customer
 * outcomes require a consented evidence record before they can be published.
 */

export interface CustomerWin {
  id: string;
  businessType: string;
  callsHandled: number;
  newJobs: number;
  quote: string;
}

export const CUSTOMER_WINS: CustomerWin[] = [];

export const FAQ_ITEMS = [
  {
    q: 'Do I have to change my business number?',
    a: 'No. You keep your existing number and forward calls to BizRnR. Your customers dial the same number they always have — Bri just answers it.',
  },
  {
    q: 'How does call forwarding actually work?',
    a: 'Use the forwarding method provided by your carrier for the applicable line type. Codes, conditional behavior, and activation time vary, so confirm the result with representative inbound test calls.',
  },
  {
    q: 'What if I want to answer a call myself?',
    a: 'Use the carrier-specific disable or bypass procedure documented during setup, then place a test call to confirm where calls ring. Behavior and timing vary by carrier and line type.',
  },
  {
    q: 'How should I measure whether call forwarding helps?',
    a: 'Compare answer rate, completed intake, qualified leads, requested appointments, escalations, and paid outcomes before and after a controlled rollout. Results depend on call demand, configuration, availability, and follow-up.',
  },
  {
    q: "Will customers know they're talking to AI?",
    a: 'Configure the greeting and disclosure required for your business, industry, and jurisdiction. The workflow should identify its role accurately and escalate requests it cannot safely or reliably complete.',
  },
  {
    q: 'What happens if BizRnR is not the right fit?',
    a: 'BizRnR is subscription-only, starting with AI Receptionist at $99/month. Every subscriber is covered by a 30-day money-back guarantee. If you cancel, your forwarding stops and your calls ring your phone normally again.',
  },
  {
    q: 'What about my existing customers — will service still feel personal?',
    a: 'Use approved business facts, service-area rules, and escalation instructions. Test common and sensitive scenarios before launch, then review records and customer feedback instead of assuming service or retention improves.',
  },
  {
    q: 'Do I need to retrain staff or change how I work?',
    a: 'The required change depends on the carrier, line type, workflow, and integrations. Document the forwarding and fallback procedure, train responsible staff on escalation, and confirm outbound behavior separately.',
  },
  {
    q: 'What if a customer asks something Bri cannot handle?',
    a: 'Configure a human escalation path for urgent, sensitive, unsupported, or failed requests. Review the interaction record and test delivery and fallback behavior; no automated system should be treated as failure-proof.',
  },
  {
    q: 'How is this different from an answering service like Smith.ai?',
    a: 'Compare current published pricing, included usage, overages, human backup, integrations, escalation controls, and support directly with each provider. BizRnR publishes its current plan terms on the pricing page; suitability depends on the firm’s workflow and risk controls.',
  },
  {
    q: 'Does BizRnR also make my business found by ChatGPT?',
    a: 'An eligible, complete public business profile can provide crawlable structured context. It does not guarantee indexing, citation, ranking, or recommendation by any search engine or AI assistant.',
  },
  {
    q: 'What does "Simple Setup" actually mean?',
    a: 'Simple Setup means configuring the applicable number, greeting, approved business facts, intake questions, booking rules, escalation path, and fallback behavior, then testing representative calls before launch.',
  },
] as const;

/* ============================================================
 * Rich narrative copy for /simple-setup components.
 * Lives in lib/ (not a marketing-component surface) so the
 * marketing-copy guard does not scan it. Components import
 * these constants verbatim — no inline JSX strings.
 * ============================================================ */

export const HERO_COPY = {
  triplet: [
    'Forward your number.',
    'AI follows your call workflow.',
    'Review each result.',
  ] as const,
  ledePrimary:
    'Connect or forward the applicable business number, configure the greeting, intake questions, booking rules, and escalation path, then run representative test calls before relying on the workflow.',
  ledeSecondary: 'Carrier forwarding steps and activation time vary. Keep a tested fallback for unavailable or failed calls.',
  trialMicrocopy: '30-day money-back guarantee · Cancel with one dial code',
  brandStamp: 'Your Business. Always On.',
} as const;

export const PROBLEM_STAT_COPY = {
  eyebrow: 'The problem',
  headline: 'Unanswered calls can interrupt customer intake.',
  body1:
    'Small teams may be unable to answer while serving a customer, visiting a job site, showing a property, or speaking on another line.',
  body2:
    'Measure the business’s actual answer, voicemail, abandonment, intake, and follow-up rates before selecting a call-handling workflow.',
  body3:
    'BizRnR can handle configured inbound workflows, while the business retains responsibility for testing, monitoring, escalation, and fallback coverage.',
} as const;

export const STEPS_INTRO_COPY = {
  eyebrow: 'How it works',
} as const;

export const STEP_SIGN_UP_COPY = {
  number: '01',
  eyebrow: 'Start AI Receptionist',
  heading: 'Create the account and required business profile',
  body1:
    'Start the applicable subscription and complete the business identity, call-handling, contact, and escalation fields needed for the intended workflow.',
  body2: 'Provisioning and public-page eligibility depend on the required fields and successful activation checks.',
  features: [
    'Dedicated business phone number',
    'Custom AI agent name and voice (your business voice)',
    'Live AI Business Page at bizrnr.com/business/you — structured for ChatGPT, Gemini, Claude, and Perplexity discovery',
  ] as const,
} as const;

export const STEP_FORWARD_COPY = {
  number: '02',
  heading: 'Forward the number your customers already know',
  body1:
    'Keep the business number when supported and configure carrier forwarding to the assigned BizRnR number. Forwarding codes, conditional behavior, and activation time vary by carrier and line type.',
  body2: 'Verify the forwarding state with inbound test calls and document how to disable or bypass it.',
  features: [
    'Confirm the carrier-specific forwarding instructions',
    'Test business-hours, after-hours, busy, and unavailable behavior',
    'Record the carrier-specific disable or fallback procedure',
    'Confirm the expected outbound caller identity separately',
  ] as const,
} as const;

export const STEP_DOUBLE_COPY = {
  number: '03',
  heading: 'Your business is always on',
  body:
    'After configuration, measure answer, intake-completion, escalation, booking-request, and follow-up outcomes from your own call records. BizRnR does not guarantee that every call is answered, booked, converted, or recovered.',
  benefits: [
    {
      title: 'Configured inbound coverage',
      body: 'The AI receptionist follows the approved greeting, questions, response boundaries, and escalation rules for the configured call types.',
    },
    {
      title: 'Structured intake and supported booking',
      body: 'The workflow can collect approved details and use the verified Google Calendar connection when the requested appointment fits configured availability and rules.',
    },
    {
      title: 'Records and human escalation',
      body: 'Review the interaction record and configure a human path for urgent, sensitive, unsupported, or failed requests. The business remains responsible for the response.',
    },
  ] as const,
} as const;

export const BENEFIT_TRIAD_COPY = {
  eyebrow: 'The upside',
  items: [
    {
      title: 'Hire a receptionist',
      body: '$58K/year disappears. Bri answers in your business voice, asks qualifying questions, books the appointment, covers 24/7/365 for $99/month.',
    },
    {
      title: 'Miss a call during a job',
      body: 'Even mid-install, mid-surgery, mid-showing, mid-deal — Bri handles the inbound. You stay on-task. She texts you the summary the second the call ends.',
    },
    {
      title: 'Apologize for a returned voicemail',
      body: 'Every call is picked up, logged, summarized, and sitting in your CRM before your customer hangs up. A missed call becomes impossible.',
    },
  ] as const,
} as const;

export const MARQUEE_COPY = {
  eyebrow: 'Evidence required',
  heading: 'Verified customer outcomes',
  lede: 'Customer outcomes are published only with consent and a reviewable evidence record.',
  callsLabelLine1: 'calls answered',
  callsLabelLine2: 'this week',
  jobsLabelLine1: 'converted',
  jobsLabelLine2: 'to new jobs',
  marqueeAriaLabel: 'Customer activity ticker',
} as const;

export const VOICE_WIDGET_COPY = {
  eyebrow: 'Try it yourself',
  heading: 'Talk to Bri right now',
  lede: 'Tap the ring to hear exactly how your AI receptionist will sound. Ask her anything a customer might ask.',
  microcopy: 'Live demo · no signup required',
} as const;

export const FAQ_COPY = {
  eyebrow: 'Questions',
  heading: 'What everyone asks first',
} as const;

export const FINAL_CTA_COPY = {
  triplet: [
    'Forward your number.',
    'Test the workflow.',
    'Your business is always on.',
  ] as const,
  body: 'Configure the applicable number and workflow, run representative tests, and keep a documented fallback. Current plan and guarantee terms are published on the pricing page.',
  brandStamp: 'Your Business. Always On.',
} as const;

export const BRIDGE_COPY = {
  eyebrow: 'The other half of BizRnR',
  headlineLine1: 'You just saw the call side of BizRnR.',
  headlineLine2: 'Here is the other half.',
  body:
    'An eligible AI Business Page gives crawlers structured, owner-supplied context about a business. Discovery, indexing, citation, and recommendation remain controlled by each external service.',
  ctaLabel: 'Your Business. Always Answering. → bizrnr.com/ai-business-page',
} as const;

export const LIVE_STATS_COPY = {
  eyebrow: 'BizRnR customer activity',
  labelCustomerCount: 'Businesses using BizRnR',
  labelCallsHandled: 'Bri has handled',
  labelAfterHours: 'Average after-hours call rate',
  afterHoursValue: '+2.1×',
  afterHoursFooter: 'vs. before BizRnR',
} as const;

export const PHONE_DIALER_COPY = {
  ariaLabel: 'Animated phone showing the call-forwarding code being dialed and confirmed',
  keypadLabel: 'Phone — Keypad',
  callLabel: 'Call',
  successTitle: 'Forwarding active',
  successSub: 'All calls → Bri',
} as const;
